// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import moment from 'moment';
import { Box, useTheme } from '@mui/system';
import { Typography, Select, MenuItem, InputBase, IconButton, Menu, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { useAppDispatch, useAppSelector } from 'Hooks';
import SearchField from 'Components/SearchField';
import OverflowTooltipText from 'Components/OverflowTooltipText';
import ReferenceIdEditor from 'Components/ReferenceIdEditor';
import ProjectPreview from 'Assets/projectPreview.png';
import { PROJECTS_SECTION_TITLE } from 'Utils/constants';
import { formatProjectPrice } from 'Utils/deliverables';
import { resolveDeliverySchedule } from 'Utils/deliverySchedule';
import { GetProjectDetailsRoute, GetProjectEsri3DMapRoute } from 'Utils/routes';
import { getPinStatusChip } from 'Features/map/EsriDashboardMap/ProjectOverviewCard/statusChip';
import { SetFoldersAction } from 'Features/folderList/folderListActions';
import {
  MarkOrderSortFilterTouchedAction, SetKeywordFilterAction, SetLoadAllAction, SetLoadNewAction,
  SetMapFocusAction, SetOrderSortFilterAction, SetPageAction, SetProjectsAction,
  SetRailViewAction, SetSelectedPinProjectAction, SetSortFilterAction,
} from '../projectListActions';
import { ReadThumbnailThunk } from '../projectListThunk';
import { selectEffectiveOrderSortFilter } from '../projectListSelector';
import { ORDER_SORT_FILTER_OPTIONS } from '../PageToolbar';
import stylesDef from './styles';

type RailStyles = ReturnType<typeof stylesDef>;

const formatDate = (raw?: string) => (raw ? moment.utc(raw).format('MMM D, YYYY') : '—');

// Client-side ordering mirroring the grid's sort dropdown for the plotted set.
const sortProjects = (projects: IProjectMapCoordinate[], sortFilter: string): IProjectMapCoordinate[] => {
  const sorted = [...projects];
  if (sortFilter === 'nameA') return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (sortFilter === 'nameD') return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
  return sorted.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
};

// memo: the rail re-renders on every projectList slice change (selection, thumbnail
// resolutions streaming in), and re-rendering every card for each of those reads as the
// whole list regenerating. A card re-renders only when its own inputs change.
const ProjectMapCard = memo(({ project, styles, isSelected, onSelect }: {
  project: IProjectMapCoordinate; styles: RailStyles; isSelected: boolean; onSelect: (selected: IProjectMapCoordinate) => void;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const status = getPinStatusChip(project);
  const price = formatProjectPrice(project.dollarCost);
  // The shared schedule owns the "does this project have a live delivery" rule (canceled
  // orders have none) - the rail and the pin card must never disagree on it.
  const hasDeliverySchedule = resolveDeliverySchedule(project).state !== 'none';
  const hasThumbnail = Boolean(project.thumbnail && !project.thumbnail.includes('undefined'));
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Raw S3 thumbnail URLs 403 against the private bucket; the grid resolves each card's image
  // through readThumbnail, so the rail does the same - lazily, once a card scrolls into view,
  // and once per session: a resolved thumbnail is kept in the store, and re-resolving would
  // mint a fresh signed URL and visibly reload an image the user already has.
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true });
  useEffect(() => {
    if (inView && hasThumbnail && !project.thumbnailResolved) dispatch(ReadThumbnailThunk(project._id));
  }, [inView, project.thumbnailResolved]);

  // A div with button semantics rather than a native button: Safari renders complex children
  // inside styled buttons unreliably (present in the DOM, painted at zero size).
  return (
    <Box
      ref={inViewRef}
      data-project-id={project._id}
      role="button"
      tabIndex={0}
      sx={[styles.card, isSelected && styles.cardSelected]}
      onClick={() => onSelect(project)}
      onKeyDown={(event: React.KeyboardEvent) => {
        // Only when the card itself is focused - the "..." menu button handles its own keys.
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) onSelect(project);
      }}
    >
      <Box sx={styles.cardBody}>
        <Box
          component="img"
          src={project.thumbnailResolved ? project.thumbnail : ProjectPreview}
          alt=""
          sx={styles.cardThumb}
          onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
            // An expired signed URL still degrades to the placeholder, not a broken image.
            event.currentTarget.src = ProjectPreview;
          }}
        />
        <Box sx={styles.textCol}>
          <Box sx={styles.nameRow}>
            <OverflowTooltipText text={project.name || 'Untitled project'} sx={styles.cardName} />
            <IconButton
              size="small"
              aria-label="Project actions"
              className="railCardMenu"
              sx={[styles.cardMenuButton, isSelected && styles.cardMenuButtonVisible]}
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
              }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              onClick={(event) => event.stopPropagation()}
            >
              {/* Links so middle-click / cmd-click open a new tab natively. The explicit close
                  matters exactly then: a new tab leaves this one open, and an unclosed Menu
                  keeps its modal backdrop blocking the page. */}
              <MenuItem component={Link} to={GetProjectDetailsRoute(project._id)} onClick={() => setMenuAnchor(null)}>Project Details</MenuItem>
              <MenuItem component={Link} to={GetProjectEsri3DMapRoute(project._id)} onClick={() => setMenuAnchor(null)}>Open in Viewer</MenuItem>
            </Menu>
          </Box>
          <Box sx={styles.statusRow}>
            <Box sx={styles.statusDot(status.kind)} />
            <Typography sx={styles.statusLabel}>{status.label}</Typography>
            {!project.middlePoint && (
              <Tooltip title="No location to map" arrow placement="top">
                <LocationOffIcon sx={styles.noLocationIcon} aria-label="No location to map" />
              </Tooltip>
            )}
          </Box>
          <Typography sx={styles.cardDates}>
            <b>Est Delivery Date:</b>
            {` ${hasDeliverySchedule ? formatDate(project.deliveryEstimate) : '—'}`}
          </Typography>
          <Typography sx={styles.cardDates}>
            <b>Due Date:</b>
            {` ${hasDeliverySchedule ? formatDate(project.deliveryDate) : '—'}`}
          </Typography>
        </Box>
      </Box>
      <Box sx={styles.cardFooter}>
        <Box sx={styles.cardRef}>
          <Box component="span">REF:</Box>
          {/* Editable in place (AUT-6449); the editor keeps its clicks and keys off the card. */}
          <ReferenceIdEditor projectId={project._id} referenceId={project.referenceId} emptyText="—" addPrompt="—" />
          <OverflowTooltipText text={`| Created at ${formatDate(project.createdAt)}`} sx={styles.cardRefCreated} />
        </Box>
        {price && <Typography sx={styles.cardPrice}>{price}</Typography>}
      </Box>
    </Box>
  );
});

// The Figma's map-view left rail: search and the sort/filter dropdowns live here (not in a
// full-width toolbar), above a scrolling card list of exactly the projects the map plots.
// Clicking a card flies the map to that project's pin and opens its overview card.
export const MapRail = () => {
  const theme = useTheme();
  const styles = useMemo(() => stylesDef(theme), [theme]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Individual field subscriptions: selecting the whole slice re-rendered the rail on every
  // slice change, including the hidden grid's per-keystroke fetch lifecycle.
  const mapCoordinates = useAppSelector((state) => state.projectList.mapCoordinates);
  const mapCoordinatesLoading = useAppSelector((state) => state.projectList.mapCoordinatesLoading);
  const keywordFilter = useAppSelector((state) => state.projectList.keywordFilter);
  const sortFilter = useAppSelector((state) => state.projectList.sortFilter);
  const orderSortFilter = useAppSelector((state) => state.projectList.orderSortFilter);
  const selectedPinProjectId = useAppSelector((state) => state.projectList.selectedPinProjectId);
  const mapCoordinatesKey = useAppSelector((state) => state.projectList.mapCoordinatesKey);
  const effectiveOrderSortFilter = useAppSelector(selectEffectiveOrderSortFilter);

  const projects = useMemo(() => sortProjects(mapCoordinates, sortFilter), [mapCoordinates, sortFilter]);

  // Incremental mounting: a large org's full portfolio in one commit (cards, observers,
  // menus) blocks the main thread for seconds while the map initializes. The first chunk
  // renders immediately; a sentinel at the list bottom mounts the rest as the user scrolls.
  // A return from details/viewer starts at the depth the user had reached.
  const CARD_CHUNK = 30;
  const railView = useAppSelector((state) => state.projectList.railView);
  const [visibleCount, setVisibleCount] = useState(() => railView?.mountedCount || CARD_CHUNK);
  const visibleCountRef = useRef(visibleCount);
  visibleCountRef.current = visibleCount;
  const { ref: mountMoreRef, inView: mountMoreInView } = useInView();
  useEffect(() => {
    if (mountMoreInView) setVisibleCount((current) => Math.min(current + CARD_CHUNK, projects.length || CARD_CHUNK));
  }, [mountMoreInView, projects.length]);

  // Selecting a pin on the map centres its card in the rail. Selections made by clicking a
  // card skip the scroll - the user is already looking at it, and re-centring would drag the
  // list out from under the cursor. Optional call: jsdom has no scrollIntoView.
  const selectionFromRailRef = useRef(false);
  const cardListRef = useRef<HTMLDivElement | null>(null);

  // Restore the scroll offset a route navigation interrupted, and capture it (plus the
  // mounted depth) when the rail unmounts - card heights are structural, so the offset maps
  // back to the same cards even before their thumbnails resolve.
  useEffect(() => {
    // Captured now: React detaches the ref before unmount cleanups run, so reading
    // cardListRef.current in the cleanup always yields null and a scrollTop of 0.
    const listNode = cardListRef.current;
    if (railView?.scrollTop && listNode) listNode.scrollTop = railView.scrollTop;
    return () => {
      dispatch(SetRailViewAction({
        scrollTop: listNode?.scrollTop || 0,
        mountedCount: visibleCountRef.current,
      }));
    };
  }, []);
  // Centers the selected card when the selection changes (map-side) or the list changes
  // under it - a sort re-ordering, or a filter/search refetch landing (mapCoordinatesKey
  // stamps each fetched set, so the re-center runs against the new list, not the dropdown
  // click) - and never when chunks merely mount during scrolling, which would yank the list
  // back to the selection mid-scroll. A selection filtered out of the set simply does not
  // center; switching back to a set that contains it centers again.
  const lastCenteredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedPinProjectId) return;
    const centerContext = `${selectedPinProjectId}|${sortFilter}|${mapCoordinatesKey}`;
    if (selectionFromRailRef.current) {
      // The user clicked this card - they are looking at it; no scroll, but this context
      // counts as centered so chunk mounts don't scroll later.
      selectionFromRailRef.current = false;
      lastCenteredRef.current = centerContext;
      return;
    }
    if (lastCenteredRef.current === centerContext) return;
    const selectedIndex = projects.findIndex((project) => project._id === selectedPinProjectId);
    if (selectedIndex === -1) return;
    // A selection can land on a card the incremental mounting hasn't reached - mount up to
    // it first; this effect re-runs once visibleCount grows and then scrolls.
    if (selectedIndex >= visibleCount) {
      setVisibleCount(selectedIndex + CARD_CHUNK);
      return;
    }
    lastCenteredRef.current = centerContext;
    const card = cardListRef.current?.querySelector(`[data-project-id="${selectedPinProjectId}"]`);
    card?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [selectedPinProjectId, sortFilter, mapCoordinatesKey, visibleCount]);

  const reloadProjects = () => {
    // Reset the grid's paging state and clear its stale data, but do NOT fetch: the rail only
    // exists in map view, where the grid renders nothing - the grid refetches page 0 when the
    // user returns to it (ProjectList's grid-return effect).
    dispatch(SetLoadNewAction(true));
    dispatch(SetLoadAllAction(false));
    dispatch(SetPageAction(0));
    dispatch(SetProjectsAction([]));
    dispatch(SetFoldersAction([]));
  };

  // Stable so memoized cards don't re-render just because the rail did.
  const selectProjectCard = useCallback((project: IProjectMapCoordinate) => {
    // A located project flies the map to its pin and opens the overview card; an unlocated
    // one has no pin, so its card goes straight to the project's details page - still marked
    // selected, so returning from that page centers and highlights the card the user left by.
    if (!project.middlePoint) {
      selectionFromRailRef.current = true;
      dispatch(SetSelectedPinProjectAction(project._id));
      navigate(GetProjectDetailsRoute(project._id));
      return;
    }
    selectionFromRailRef.current = true;
    dispatch(SetSelectedPinProjectAction(project._id));
    dispatch(SetMapFocusAction({ kind: 'project', id: project._id }));
  }, []);

  return (
    <Box sx={styles.root}>
      <SearchField
        placeholder="Search by projects and folders"
        value={keywordFilter}
        onChange={(value: string) => {
          dispatch(SetKeywordFilterAction(value));
          reloadProjects();
        }}
      />
      <Box sx={styles.headerRow}>
        <Typography sx={styles.headerTitle}>{PROJECTS_SECTION_TITLE[effectiveOrderSortFilter]}</Typography>
        <Select
          inputProps={{ name: 'sort', id: 'map-rail-sort' }}
          input={<InputBase />}
          sx={styles.headerSelect}
          value={sortFilter}
          onChange={(event) => {
            dispatch(SetSortFilterAction(event.target.value));
            reloadProjects();
          }}
        >
          <MenuItem value="updatedAtD">Recent</MenuItem>
          <MenuItem value="nameA">A-Z</MenuItem>
          <MenuItem value="nameD">Z-A</MenuItem>
        </Select>
        <Select
          inputProps={{ name: 'filterby', id: 'map-rail-filterby' }}
          input={<InputBase />}
          sx={styles.headerSelect}
          value={orderSortFilter}
          onOpen={() => dispatch(MarkOrderSortFilterTouchedAction())}
          onChange={(event) => {
            dispatch(MarkOrderSortFilterTouchedAction());
            dispatch(SetOrderSortFilterAction(event.target.value as ProjectOrderSortFilter));
            reloadProjects();
          }}
        >
          {ORDER_SORT_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box ref={cardListRef} sx={styles.cardList}>
        {projects.slice(0, visibleCount).map((project) => (
          <ProjectMapCard
            key={project._id}
            project={project}
            styles={styles}
            isSelected={project._id === selectedPinProjectId}
            onSelect={selectProjectCard}
          />
        ))}
        {visibleCount < projects.length && <Box ref={mountMoreRef} />}
        {!mapCoordinatesLoading && projects.length === 0 && (
          <Box sx={styles.emptyState}>
            <Typography variant="body1">No projects to show</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MapRail;
